import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  CornerDownLeft,
  LayoutDashboard,
  Plus,
  Search,
  Target,
  TrendingUp,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { searchApi, type SearchResult } from '@/services/search.api';
import { useFocusTrap } from '@/hooks/useFocusTrap';

/**
 * Palette de commandes ⌘K - recherche globale unifiée.
 *
 * Contenu :
 *  - Actions statiques (« Nouveau prospect », navigation)
 *  - Recherche globale (prospects, opportunités, contacts) via `/api/v1/search`
 *    avec debounce 250 ms et groupement par type (SPECIFICATIONS.md §8.1).
 *
 * Navigation : ↑↓ déplacent la sélection, Enter valide, Esc ferme. Focus
 * trappé dans la modale. Pas de rendu HTML dangereux - tous les champs
 * passent par JSX (auto-escape).
 */

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Ouvre la modale de création d'opportunité (gérée au niveau AppLayout). */
  onCreateOpportunity?: () => void;
}

type CommandItem =
  | {
      id: string;
      kind: 'action';
      label: string;
      hint?: string;
      icon: React.ComponentType<{ className?: string }>;
      run: () => void;
    }
  | {
      id: string;
      kind: 'result';
      resultType: SearchResult['type'];
      label: string;
      hint: string;
      icon: React.ComponentType<{ className?: string }>;
      run: () => void;
    };

interface Section {
  title: string;
  items: CommandItem[];
}

const RESULT_ICONS: Record<SearchResult['type'], React.ComponentType<{ className?: string }>> = {
  prospect: Building2,
  opportunity: Target,
  contact: User,
};

const RESULT_SECTION_TITLES: Record<SearchResult['type'], string> = {
  prospect: 'Prospects',
  opportunity: 'Opportunités',
  contact: 'Contacts',
};

const MIN_QUERY_LENGTH = 3;

export function CommandPalette({ open, onClose, onCreateOpportunity }: CommandPaletteProps) {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.accessToken);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{
    prospects: SearchResult[];
    opportunities: SearchResult[];
    contacts: SearchResult[];
  }>({ prospects: [], opportunities: [], contacts: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);

  useFocusTrap(open, containerRef);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults({ prospects: [], opportunities: [], contacts: [] });
    setSelected(0);
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(t);
  }, [open]);

  // Debounced global search (min 3 caractères).
  useEffect(() => {
    if (!open || !token) return;
    const term = query.trim();
    if (term.length < MIN_QUERY_LENGTH) {
      setResults({ prospects: [], opportunities: [], contacts: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await searchApi.global(token, term);
        setResults(res.groups);
      } catch {
        setResults({ prospects: [], opportunities: [], contacts: [] });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, open, token]);

  const actions: CommandItem[] = useMemo(
    () => [
      {
        id: 'act:new-prospect',
        kind: 'action',
        label: 'Créer un prospect',
        hint: 'Ouvre la modale de création',
        icon: Plus,
        run: () => {
          onClose();
          navigate('/prospects?new=1');
        },
      },
      ...(onCreateOpportunity
        ? [
            {
              id: 'act:new-opportunity',
              kind: 'action' as const,
              label: 'Créer une opportunité',
              hint: 'Nouvelle affaire commerciale',
              icon: Target,
              run: () => {
                onClose();
                onCreateOpportunity();
              },
            },
          ]
        : []),
      {
        id: 'act:goto-pipeline',
        kind: 'action',
        label: 'Aller au pipeline',
        hint: '/pipeline',
        icon: TrendingUp,
        run: () => {
          onClose();
          navigate('/pipeline');
        },
      },
      {
        id: 'act:goto-prospects',
        kind: 'action',
        label: 'Aller aux prospects',
        hint: '/prospects',
        icon: Building2,
        run: () => {
          onClose();
          navigate('/prospects');
        },
      },
      {
        id: 'act:goto-dashboard',
        kind: 'action',
        label: 'Aller au dashboard',
        hint: '/',
        icon: LayoutDashboard,
        run: () => {
          onClose();
          navigate('/');
        },
      },
    ],
    [navigate, onClose, onCreateOpportunity],
  );

  const filteredActions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter((a) => a.label.toLowerCase().includes(q));
  }, [actions, query]);

  const sections: Section[] = useMemo(() => {
    const out: Section[] = [];
    if (filteredActions.length > 0) {
      out.push({ title: 'Actions', items: filteredActions });
    }
    const typeOrder: SearchResult['type'][] = ['prospect', 'opportunity', 'contact'];
    const groupsByType = {
      prospect: results.prospects,
      opportunity: results.opportunities,
      contact: results.contacts,
    };
    for (const t of typeOrder) {
      const list = groupsByType[t];
      if (list.length === 0) continue;
      out.push({
        title: `${RESULT_SECTION_TITLES[t]} (${list.length})`,
        items: list.map<CommandItem>((r) => ({
          id: `${r.type}:${r.id}`,
          kind: 'result',
          resultType: r.type,
          label: r.title,
          hint: r.subtitle ?? '',
          icon: RESULT_ICONS[r.type],
          run: () => {
            onClose();
            navigate(r.link);
          },
        })),
      });
    }
    return out;
  }, [filteredActions, results, navigate, onClose]);

  const flat: CommandItem[] = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  useEffect(() => {
    setSelected((s) => Math.min(s, Math.max(0, flat.length - 1)));
  }, [flat.length]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected((s) => Math.min(s + 1, flat.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((s) => Math.max(s - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        flat[selected]?.run();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flat, selected, onClose]);

  if (!open) return null;

  const term = query.trim();
  const showEmpty =
    term.length >= MIN_QUERY_LENGTH &&
    !loading &&
    filteredActions.length === 0 &&
    results.prospects.length === 0 &&
    results.opportunities.length === 0 &&
    results.contacts.length === 0;

  let runningIndex = 0;

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-start justify-center bg-brand/30 px-4 pt-[15vh] backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Palette de commandes"
        aria-modal="true"
        className="shadow-modal animate-scale-in w-full max-w-[560px] overflow-hidden rounded-lg bg-white"
      >
        <div className="border-border-subtle flex items-center gap-3 border-b px-4">
          <Search className="text-text-muted h-4 w-4 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher prospect, opportunité, contact…"
            // `focus-visible:outline-none` surcharge la règle globale de
            // `globals.css` qui pose un outline accent 2 px pour l'a11y
            // clavier. Ici l'input est déjà visuellement différencié par
            // son placement dans la modale, l'outline violet est redondant.
            className="text-brand placeholder:text-text-muted h-12 w-full border-0 bg-transparent text-sm outline-none focus:ring-0 focus-visible:outline-none"
          />
        </div>

        <div className="max-h-[440px] overflow-y-auto">
          {loading && term.length >= MIN_QUERY_LENGTH && <SearchSkeleton />}
          {term.length > 0 && term.length < MIN_QUERY_LENGTH && (
            <div className="text-text-muted px-4 py-8 text-center text-sm">
              Tapez au moins {MIN_QUERY_LENGTH} caractères pour rechercher.
            </div>
          )}
          {showEmpty && (
            <div className="text-text-muted px-4 py-8 text-center text-sm">
              Aucun résultat pour « {term} ».
            </div>
          )}
          {!loading &&
            sections.map((section) => (
              <div key={section.title} className="border-border-subtle border-b last:border-0">
                <div className="text-text-muted px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider">
                  {section.title}
                </div>
                <ul>
                  {section.items.map((item) => {
                    const index = runningIndex++;
                    const isSelected = index === selected;
                    const Icon = item.icon;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onMouseEnter={() => setSelected(index)}
                          onClick={item.run}
                          className={cn(
                            'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                            isSelected ? 'bg-accent/10' : 'hover:bg-gray-50',
                          )}
                        >
                          <Icon
                            className={cn(
                              'h-4 w-4 shrink-0',
                              isSelected ? 'text-accent' : 'text-text-muted',
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-brand truncate font-medium">{item.label}</div>
                            {item.hint && (
                              <div className="text-text-muted truncate text-xs">{item.hint}</div>
                            )}
                          </div>
                          {isSelected && (
                            <CornerDownLeft className="text-accent h-3.5 w-3.5 shrink-0" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
        </div>

        <div className="border-border-subtle text-text-muted flex items-center justify-between border-t bg-gray-50 px-4 py-2 text-[11px]">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd> naviguer
            </span>
            <span className="flex items-center gap-1">
              <Kbd>↵</Kbd> sélectionner
            </span>
          </span>
          <span className="flex items-center gap-1">
            <Kbd>esc</Kbd> fermer
          </span>
        </div>
      </div>
    </div>
  );
}

function SearchSkeleton() {
  return (
    <div className="space-y-3 px-4 py-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-gray-200" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 animate-pulse rounded bg-gray-200" />
            <div className="h-2.5 w-2/3 animate-pulse rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="border-border-subtle text-text-muted inline-flex h-5 min-w-[20px] items-center justify-center rounded border bg-white px-1 font-mono text-[10px] font-medium shadow-sm">
      {children}
    </kbd>
  );
}
